import {
  makeStyles,
  Step,
  StepContent,
  Stepper,
  Typography,
} from '@material-ui/core';
import { Check } from '@material-ui/icons';
import { FormEvent, ReactNode, useEffect, useState } from 'react';
import { Label } from './Label';
import { Header } from './Header';
import { StepButtons } from './StepButtons';
import { smScreen } from '../../lib/constants';
import { useOperator } from '../../contexts/OperatorContext';
import { Card } from '../Card';
import { useMediaQuery } from '../../hooks/useMediaQuery';

interface Props {
  canSubmit: boolean;
  currentStep?: number;
  customActions?: ReactNode;
  formActive: boolean;
  header: string | ReactNode;
  onReset?(): void;
  orientation?: 'vertical' | 'horizontal';
  onSubmit(event: FormEvent<HTMLFormElement>): void;
  steps: StepDefinition[];
  submitButton?: string;
}

export const CustomStepper = ({
  currentStep = 0,
  customActions,
  canSubmit,
  formActive,
  header,
  onReset,
  orientation = 'vertical',
  onSubmit,
  steps,
  submitButton = 'Submit',
}: Props) => {
  const [activeStep, setActiveStep] = useState(currentStep);
  const [completedSteps, setCompletedSteps] = useState<number[]>([]);
  const [expandAll, setExpandAll] = useState(false);
  const operator = useOperator();
  const isMobile = useMediaQuery(smScreen);
  const classes = useStyles(isMobile, orientation)();

  useEffect(() => {
    handleReset();
  }, [operator.id]);

  useEffect(() => {
    if (steps[activeStep].validation && !completedSteps.includes(activeStep))
      setCompletedSteps([...completedSteps, activeStep]);
    else if (
      !steps[activeStep].validation &&
      completedSteps.includes(activeStep)
    )
      setCompletedSteps(completedSteps.filter((step) => step !== activeStep));
  }, [steps, activeStep]);

  useEffect(() => {
    if (expandAll) {
      setCompletedSteps(
        steps.reduce((arr: number[], step, index) => {
          if (step.validation) arr.push(index);
          return arr;
        }, [])
      );
    }
  }, [currentStep, expandAll]);

  const defaultIcon = (step: number) => () =>
    (
      <Typography className={classes.icon} align="center">
        {step}
      </Typography>
    );

  const completedIcon = () => (
    <Check className={classes.completedIcon} fontSize="small" />
  );

  const handleReset = () => {
    setCompletedSteps([]);
    setActiveStep(0);
    if (onReset !== undefined) onReset();
  };

  const content = (index: number) => (
    <Card fullWidth>
      <div className={classes.content}>{steps[index].content}</div>
      <StepButtons
        activeStep={activeStep}
        canSubmit={canSubmit}
        completedSteps={completedSteps}
        expandAll={expandAll}
        onChangeStep={setActiveStep}
        orientation={orientation}
        onSubmit={onSubmit}
        onSkipToEnd={() =>
          setCompletedSteps(
            steps.reduce((arr: number[], step, index) => {
              if (step.id !== steps.length) arr.push(index);
              return arr;
            }, [])
          )
        }
        stepIndex={index}
        steps={steps}
        submitButton={submitButton}
      />
    </Card>
  );

  return (
    <form
      className={classes.container}
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit(event);
      }}
    >
      <Header
        customActions={customActions}
        disabled={!formActive}
        expandAll={expandAll}
        header={header}
        onReset={handleReset}
        orientation={orientation}
        onToggle={() => setExpandAll(!expandAll)}
      />
      <Stepper
        classes={{
          root: classes.root,
        }}
        alternativeLabel={orientation === 'horizontal'}
        orientation={orientation}
      >
        {steps.map((step, index) => (
          <Step
            key={`${header}-step-${step.id}`}
            active={activeStep === index}
            expanded={expandAll}
          >
            <Label
              activeStep={activeStep}
              description={step.description}
              expandAll={expandAll}
              label={step.label}
              onStepChange={setActiveStep}
              orientation={orientation}
              stepIcon={
                completedSteps.includes(index)
                  ? completedIcon
                  : defaultIcon(step.id)
              }
              stepIndex={index}
            />
            {orientation === 'vertical' ? (
              <StepContent>{content(index)}</StepContent>
            ) : null}
          </Step>
        ))}
      </Stepper>
      {orientation === 'horizontal' ? (
        <Typography className={classes.description} gutterBottom>
          {steps[activeStep].description}
        </Typography>
      ) : null}
      {orientation === 'horizontal' ? content(activeStep) : null}
    </form>
  );
};
const useStyles = (isMobile: boolean, orientation: 'vertical' | 'horizontal') =>
  makeStyles((theme) => {
    const iconSize = 32;
    return {
      completedIcon: {
        width: iconSize,
        height: iconSize,
        borderRadius: iconSize,
        padding: theme.spacing(0.8),
        backgroundColor: theme.palette.primary.main,
      },
      container: {
        display: 'flex',
        flexDirection: 'column',
        minWidth: 300,
        maxWidth: orientation === 'vertical' ? 800 : 1000,
        width: '100%',
      },
      content: {
        minHeight: 200,
        height: 'fit-content',
        padding: theme.spacing(2),
        overflow: 'auto',
      },
      description: {
        opacity: 0.7,
        fontSize: 12,
      },
      icon: {
        width: iconSize,
        height: iconSize,
        borderRadius: iconSize,
        padding: theme.spacing(0.5),
        border: `1px solid ${theme.palette.primary.main}`,
      },
      root: {
        paddingInline: isMobile || orientation === 'horizontal' ? 0 : undefined,
        backgroundColor: 'transparent',
        '& .MuiStepConnector-lineVertical': {
          borderLeft: isMobile ? 'none' : undefined,
        },
        '& .MuiStepConnector-root': {
          marginLeft: isMobile ? 0 : orientation === 'vertical' ? 15 : 'unset',
        },
        '& .MuiStepContent-root': {
          marginLeft: isMobile ? 0 : orientation === 'vertical' ? 15 : 'unset',
          borderLeft: isMobile ? 'none' : undefined,
          padding: isMobile ? 0 : undefined,
        },
        '& .MuiStepIcon-text': {
          display: 'block !important',
        },
      },
    };
  });
